import type { DmxPatch, Item } from '../model/schema.js';

const MODE_CHANNELS = /(\d{1,3})\s*-?\s*(?:ch\b|channels?\b|kan(?:al|äle)\b|kanäle)/iu;

/** Channels a fixture occupies: explicit footprint, else from the mode text ("16ch"), else 1. */
export function dmxFootprint(dmx: DmxPatch): number {
  if (dmx.footprint !== undefined) return dmx.footprint;
  const m = MODE_CHANNELS.exec(dmx.mode ?? '');
  const n = m === null ? 1 : Number(m[1]);
  return Math.min(512, Math.max(1, n));
}

/** A fixture with universe and start address. */
type Patched = DmxPatch & { universe: number; address: number };

const isPatched = (dmx: DmxPatch | undefined): dmx is Patched =>
  dmx?.universe !== undefined && dmx.address !== undefined;

/** First and last DMX address (inclusive); the last may exceed 512 (= does not fit). */
export function dmxRange(dmx: Patched): [number, number] {
  return [dmx.address, dmx.address + dmxFootprint(dmx) - 1];
}

export interface DmxConflict {
  universe: number;
  /** Fixtures whose ranges overlap (sorted by address), or one that runs past 512. */
  itemIds: string[];
  kind: 'overlap' | 'overflow';
}

/** Overlapping addresses per universe and fixtures that do not fit into 512 channels. */
export function dmxConflicts(items: readonly Item[]): DmxConflict[] {
  const byUniverse = new Map<number, { id: string; from: number; to: number }[]>();
  const out: DmxConflict[] = [];
  for (const i of items) {
    const dmx = i.light?.dmx;
    if (!isPatched(dmx)) continue;
    const [from, to] = dmxRange(dmx);
    if (to > 512) out.push({ universe: dmx.universe, itemIds: [i.id], kind: 'overflow' });
    byUniverse.set(dmx.universe, [...(byUniverse.get(dmx.universe) ?? []), { id: i.id, from, to }]);
  }
  for (const [universe, list] of [...byUniverse].sort((a, b) => a[0] - b[0])) {
    list.sort((a, b) => a.from - b.from || a.id.localeCompare(b.id));
    let group: typeof list = [];
    let end = 0;
    const flush = () => {
      if (group.length > 1)
        out.push({ universe, itemIds: group.map((g) => g.id), kind: 'overlap' });
    };
    for (const f of list) {
      if (group.length > 0 && f.from <= end) {
        group.push(f);
        end = Math.max(end, f.to);
      } else {
        flush();
        group = [f];
        end = f.to;
      }
    }
    flush();
  }
  return out;
}

/** Next free start address in a universe for a fixture of `footprint` channels, or null. */
export function nextFreeAddress(
  items: readonly Item[],
  universe: number,
  footprint: number,
): number | null {
  const used = items
    .flatMap((i) => {
      const dmx = i.light?.dmx;
      return isPatched(dmx) && dmx.universe === universe ? [dmxRange(dmx)] : [];
    })
    .sort((a, b) => a[0] - b[0]);
  let candidate = 1;
  for (const [from, to] of used) {
    if (candidate + footprint - 1 < from) break;
    candidate = Math.max(candidate, to + 1);
  }
  return candidate + footprint - 1 <= 512 ? candidate : null;
}
